import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './containers/App';
import {ApiProvider} from "@reduxjs/toolkit/dist/query/react";
import {Provider, useSelector} from "react-redux";
import {BrowserRouter} from "react-router-dom";
import api from "./core/api/base-api";
import {store} from "./core/configureStore";
import EnvironmentProvider from "./containers/EnvironmentProvider";
import {SnackBarProvider} from "./core/context/snackBarProvider";

import {PublicClientApplication, EventType} from '@azure/msal-browser';
import {createMsalTokenRefresher, refreshTokenOnce, setTokenRefresher} from "./core/auth/tokenRefresh";
import {isTokenExpired} from "./components/Utils/CommonUtils";
import * as Sentry from "@sentry/react";
import {configs, getDomainAlias} from "./constants";

const msalConfig = configs[getDomainAlias()]['msal']

Sentry.setUser({ email: localStorage.getItem("username") });


/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize()

let refreshInterval = null
const startRefreshInterval = () => {
    if (refreshInterval == null) {
        refreshInterval = setInterval(() => refreshTokenOnce({force: true}), 600000);
    }
}

// Listen for sign-in event and set active account.
// Регистрируем до handleRedirectPromise, чтобы результат возврата после redirect тоже попал в store.
msalInstance.addEventCallback((event) => {

    if (
        (event.eventType === EventType.LOGIN_SUCCESS
            || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
            || event.eventType === EventType.SSO_SILENT_SUCCESS
        ) && event.payload.account
    ) {
        const account = event.payload.account;
        msalInstance.setActiveAccount(account);

        store.dispatch({
            type: 'AUTH_SELF_SUCCESS', payload: {
                data: account.idTokenClaims.roles,
                token: event.payload.idToken,
                email: account.username
            }
        });
        localStorage.setItem("azureToken", event.payload.idToken)
        localStorage.setItem("username", account.username)

        if (event.eventType === EventType.LOGIN_SUCCESS) startRefreshInterval()
    }
    switch (event.eventType) {
        case EventType.ACQUIRE_TOKEN_FAILURE:
            Sentry.captureMessage(
                `Token refresh failed ` + JSON.stringify(event.error),
                Sentry.Severity.Warning
            )
            break;
        case EventType.LOGOUT_SUCCESS:
            store.dispatch({
                type: 'AUTH_LOGOUT'
            })
            clearInterval(refreshInterval)
            refreshInterval = null
            localStorage.setItem('azureToken', '')
            break;
    }
});

// Обрабатываем ответ после acquireTokenRedirect/loginRedirect ДО любых silent-запросов:
// иначе флаг interaction_in_progress остаётся висеть и следующий popup/redirect падает или
// запускает ещё одну перезагрузку. MsalProvider позже получит тот же (закешированный) результат.
try {
    const redirectResult = await msalInstance.handleRedirectPromise();
    if (redirectResult?.account) msalInstance.setActiveAccount(redirectResult.account);
} catch (e) {
    console.error('handleRedirectPromise error', e);
    Sentry.captureMessage(`handleRedirectPromise ${JSON.stringify(e)}`, Sentry.Severity.Warning);
}

// Default to using the first account if no account is active on page load
if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

const activeAccount = msalInstance.getActiveAccount();

setTokenRefresher(createMsalTokenRefresher({
    msalInstance,
    dispatch: store.dispatch,
    storage: localStorage,
    sentry: Sentry,
    origin: document.location.origin,
}));

// совместимость: внешний код может попросить обновить токен через событие
window.addEventListener('onTokenExpired', () => refreshTokenOnce());

if(activeAccount != null) {
    if(activeAccount.hasOwnProperty('idTokenClaims')) {
        store.dispatch({
            type: 'AUTH_SELF_SUCCESS', payload: {
                data: activeAccount.idTokenClaims.roles,
                email: activeAccount.username
            }
        });
    }

    Sentry.setUser({ email: activeAccount.username });

    // Если сохранённый токен ещё валиден (>5 минут) — не блокируем первый рендер походом в Azure,
    // обновляем в фоне. Запросы, получившие 401, сами дождутся обновления и повторятся (withAuthRetry).
    const cachedToken = localStorage.getItem('azureToken')
    if (!cachedToken || isTokenExpired(cachedToken)) {
        await refreshTokenOnce()
    } else {
        refreshTokenOnce()
    }
    startRefreshInterval()
}



const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <ApiProvider api={api}>
        <Provider store={store}>
            <BrowserRouter>
                <SnackBarProvider>
                    <EnvironmentProvider>
                        <App instance={msalInstance} key={store.getState().data.UserSession.authenticated&&store.getState().data.UserSession.fetching} />
                    </EnvironmentProvider>
                </SnackBarProvider>
            </BrowserRouter>
        </Provider>
    </ApiProvider>
);